import React, { useState, useContext, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { VsCodeAuthContext } from '../../contextApis/vsCodeAuthContext';
import { Eye, EyeOff, CheckCircle2, X } from 'lucide-react';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { cn } from '../../lib/utils';
import { motion, AnimatePresence } from 'framer-motion';

const ResetPassword = () => {
  const location = useLocation();
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const { loading, handleResetPassword } = useContext(VsCodeAuthContext);

  useEffect(() => {
    if (location.state?.email) {
      setEmail(location.state.email);
    }
  }, [location.state]);

  const getPasswordStrength = () => {
    if (!password) return null;
    let score = 0;
    if (password.length >= 8) score++;
    if (password.length >= 12) score++;
    if (/[A-Z]/.test(password) && /[a-z]/.test(password)) score++;
    if (/\d/.test(password)) score++;
    if (/[^A-Za-z0-9]/.test(password)) score++;

    if (score <= 2) return 'weak';
    if (score <= 4) return 'medium';
    return 'strong';
  };

  const passwordRequirements = [
    { id: 'length', met: password.length >= 8, text: 'At least 8 characters' },
    { id: 'uppercase', met: /[A-Z]/.test(password), text: 'One uppercase letter' },
    { id: 'lowercase', met: /[a-z]/.test(password), text: 'One lowercase letter' },
    { id: 'number', met: /\d/.test(password), text: 'One number' },
    { id: 'special', met: /[^A-Za-z0-9]/.test(password), text: 'One special character' },
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password !== confirmPassword) {
      return;
    }
    handleResetPassword(email, code, password);
  };

  const isFormValid = email && code && password && confirmPassword && password === confirmPassword &&
                      passwordRequirements.every(req => req.met);

  return (
    <form onSubmit={handleSubmit} className="space-y-4 w-full max-w-md mx-auto">
      <div className="space-y-4">
        <Input
          type="email"
          placeholder="Email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="bg-black/20 border-white/10"
        />
        <Input
          type="text"
          placeholder="Reset Code"
          value={code}
          onChange={(e) => setCode(e.target.value)}
          required
          className="bg-black/20 border-white/10"
        />
        
        <div className="relative">
          <Input
            type={showPassword ? 'text' : 'password'}
            placeholder="New Password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            className="bg-black/20 border-white/10 pr-10"
          />
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-white"
          >
            {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
          </button>
        </div>

        <AnimatePresence>
          {password && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="p-3 bg-white/5 rounded-lg border border-white/5 space-y-2 overflow-hidden"
            >
              <div className="flex justify-between items-center text-xs">
                <span className="text-muted-foreground uppercase tracking-wider">Strength</span>
                <span className={cn(
                  "font-bold uppercase",
                  getPasswordStrength() === 'weak' ? 'text-red-500' :
                  getPasswordStrength() === 'medium' ? 'text-yellow-500' : 'text-green-500'
                )}>
                  {getPasswordStrength()}
                </span>
              </div>
              <div className="h-1 w-full bg-white/10 rounded-full overflow-hidden">
                <div 
                  className={cn(
                    "h-full transition-all duration-500 ease-out",
                    getPasswordStrength() === 'weak' ? 'w-1/3 bg-red-500' :
                    getPasswordStrength() === 'medium' ? 'w-2/3 bg-yellow-500' : 'w-full bg-green-500'
                  )}
                />
              </div>
              <div className="space-y-1">
                {passwordRequirements.map((req) => (
                  <div key={req.id} className={cn(
                    "flex items-center gap-2 text-xs transition-colors",
                    req.met ? "text-green-500" : "text-muted-foreground/50"
                  )}>
                    {req.met ? <CheckCircle2 className="w-3 h-3" /> : <X className="w-3 h-3" />}
                    {req.text}
                  </div>
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        <div className="relative">
          <Input
            type={showConfirmPassword ? 'text' : 'password'}
            placeholder="Confirm New Password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            required
            className={cn(
              "bg-black/20 border-white/10 pr-10",
              confirmPassword && password !== confirmPassword && "border-red-500/50"
            )}
          />
          <button
            type="button"
            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-white"
          >
            {showConfirmPassword ? <EyeOff size={16} /> : <Eye size={16} />}
          </button>
        </div>
        {confirmPassword && password !== confirmPassword && (
          <p className="text-xs text-red-400 pl-1">Passwords do not match</p>
        )}
      </div>

      <Button 
        type="submit" 
        disabled={loading || !isFormValid}
        className="w-full bg-gradient-to-r from-primary to-purple-600 hover:opacity-90"
      >
        {loading ? 'Resetting...' : 'Reset Password'}
      </Button>
    </form>
  );
};

export default ResetPassword;
